import { Field } from "@base-ui/react/field";
import { Input as InputPrimitive } from "@base-ui/react/input";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { FieldLabel } from "../field-label/field-label";
import { Icon, type IconName } from "../icon";
import { IconButton } from "../icon-button";

const inputContainerVariants = cva(
  [
    "flex",
    "items-center",
    "gap-(--spacing-4)",
    "w-full",
    "rounded-(--radius-sm)",
    "border",
    "border-solid",
    "border-input-default-outline",
    "bg-input-default-background",
    "text-input-text-default",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "[&:not(:focus-within)]:hover:bg-input-hover-background",
    "[&:not(:focus-within)]:hover:border-input-hover-outline",
    "active:border-input-pressed-outline",
    "focus-within:border-input-active-outline",
    "[&_svg]:pointer-events-none",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      size: {
        sm: ["h-(--spacing-32)", "px-(--spacing-10)", "py-(--spacing-6)"],
        md: ["h-(--spacing-40)", "px-(--spacing-10)", "py-(--spacing-10)"],
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

export type InputTrailingAction = {
  icon: IconName;
  "aria-label": string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  "aria-pressed"?: React.AriaAttributes["aria-pressed"];
};

export interface InputProps
  extends Omit<React.ComponentPropsWithoutRef<"input">, "size" | "className"> {
  /** Visual size of the input. Defaults to "md". */
  size?: "sm" | "md";
  /** Label text rendered above the input. Omit to hide the label. */
  label?: string;
  /**
   * Icon displayed on the leading (left) side of the input.
   * Mutually exclusive with `trailingIcon`. If both are provided,
   * `leadingIcon` takes precedence.
   */
  leadingIcon?: IconName;
  /**
   * Icon displayed on the trailing (right) side of the input.
   * Mutually exclusive with `leadingIcon`, `trailingAction`, and `unit`.
   */
  trailingIcon?: IconName;
  /** Icon-only action displayed on the trailing side of the input. */
  trailingAction?: InputTrailingAction;
  /**
   * Unit label displayed on the trailing side (e.g. "kg", "USD").
   * Mutually exclusive with `trailingAction` and `trailingIcon`.
   */
  unit?: string;
  /** Helper text shown below the input. Replaced by `error` when present. */
  description?: string;
  /**
   * Error message. Presence of this prop sets the input to error state
   * and sets `aria-invalid` automatically.
   */
  error?: string;
  /** Tooltip content displayed as an info icon in the label row. */
  tooltip?: string;
  /** Reserve helper/error text space to prevent layout shift. Defaults to true. */
  reserveSubtextSpace?: boolean;
}

/**
 * Text input with label, helper text, error state, and icon support.
 *
 * @example
 * <Input label="Email" type="email" placeholder="you@example.com" />
 * <Input label="Weight" unit="kg" />
 * <Input label="Username" error="Username is taken" />
 */
function Input({
  size = "md",
  label,
  leadingIcon,
  trailingIcon,
  trailingAction,
  unit,
  description,
  error,
  tooltip,
  reserveSubtextSpace = true,
  required,
  disabled,
  ...props
}: InputProps) {
  const hasError = Boolean(error);
  const resolvedTrailingIcon =
    leadingIcon || trailingAction || unit ? undefined : trailingIcon;

  return (
    <Field.Root
      invalid={hasError}
      disabled={disabled}
      className="flex flex-col w-full"
    >
      {label && (
        <FieldLabel label={label} required={required} tooltip={tooltip} />
      )}

      <div
        className={cn(
          inputContainerVariants({ size }),
          disabled && "opacity-50",
          hasError && [
            "border-input-error-outline",
            "[&:not(:focus-within)]:hover:border-input-error-outline",
          ],
        )}
      >
        {leadingIcon && <Icon name={leadingIcon} size="sm" tone="inherit" />}

        <InputPrimitive
          required={required}
          aria-required={required || undefined}
          className={cn(
            "flex-1",
            "min-w-0",
            "outline-none",
            "text-(length:--font-size-body-medium)",
            "text-input-text-default",
            "leading-(--font-line-height-20)",
            "placeholder:text-text-subtle",
            hasError && "text-input-error-text",
          )}
          {...props}
        />

        {trailingAction ? (
          <IconButton
            {...trailingAction}
            disabled={disabled || trailingAction.disabled}
            size="sm"
            type="button"
            variant="ghost"
          />
        ) : unit ? (
          <div className="flex items-center gap-(--spacing-6) shrink-0">
            <span
              aria-hidden="true"
              className="text-input-default-outline select-none"
            >
              |
            </span>
            <span className="text-(length:--font-size-body-medium) text-input-text-supporting whitespace-nowrap">
              {unit}
            </span>
          </div>
        ) : resolvedTrailingIcon ? (
          <Icon name={resolvedTrailingIcon} size="sm" tone="inherit" />
        ) : null}
      </div>

      {(reserveSubtextSpace || hasError || description) && (
        <div
          data-testid="input-subtext-row"
          className={cn(
            "mt-(--spacing-4)",
            reserveSubtextSpace && "min-h-(--spacing-20)",
          )}
        >
          {hasError ? (
            <Field.Error
              match
              className="text-(length:--font-size-label-small) leading-(--font-line-height-16) text-input-error-text"
            >
              {error}
            </Field.Error>
          ) : description ? (
            <Field.Description className="text-(length:--font-size-label-small) leading-(--font-line-height-16) text-input-text-supporting">
              {description}
            </Field.Description>
          ) : null}
        </div>
      )}
    </Field.Root>
  );
}

export { Input };
